import React, {useMemo} from 'react'

import {noop} from 'es-toolkit'

import './date-input-field.scss'

import useUniqueId from '../../hooks/use-unique-id'
import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    apiRef?: React.RefObject<DateInputFieldHandles | null>
    id?: string
    name?: string
    value: string // ISO date
    min?: string // ISO date
    max?: string // ISO date
    appearance?: 'base' | 'error'
    disabled?: boolean
    onChange?: (
      value: string,
      name: string | undefined,
      event: React.ChangeEvent<HTMLInputElement>
    ) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  }
>

export interface DateInputFieldHandles {
  focus: () => void
}

// HTML date input requires date as string formatted as YYYY-MM-DD
const formatDateToHTMLDateInputFormat = (date: string) => date.split('T')[0]

const DateInputField = ({
  className,
  id,
  name,
  value,
  min,
  max = new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
  appearance = 'base',
  disabled = false,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  apiRef,
  ...props
}: Props) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const actualId = useUniqueId(id)
  const [focus, setFocus] = React.useState(false)

  const memoizedMin = useMemo(() => (min ? formatDateToHTMLDateInputFormat(min) : ''), [min])
  const memoizedMax = useMemo(() => (max ? formatDateToHTMLDateInputFormat(max) : ''), [max])
  const formattedValue =
    typeof value === 'string' && value !== '' ? formatDateToHTMLDateInputFormat(value) : ''

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true)
    onFocus(event)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocus(false)
    onBlur(event)
  }

  const handleContainerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      inputRef.current &&
      !(event.target as HTMLElement).closest('a') &&
      !(event.target as HTMLElement).closest('button')
    ) {
      inputRef.current.focus()
    }
  }

  React.useImperativeHandle(apiRef, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    },
  }))

  return (
    <div
      {...props}
      className={cn(
        'DateInputField',
        {
          [`appearance-${appearance}`]: !!appearance,
          disabled,
          focus,
          empty: !value,
        },
        className
      )}
      onClick={handleContainerClick}
    >
      <div className="DateInputField__main">
        <input
          className="DateInputField__input"
          name={name}
          id={actualId}
          ref={inputRef}
          type="date"
          value={formattedValue}
          min={memoizedMin}
          max={memoizedMax}
          disabled={disabled}
          onChange={event => onChange(event.target.value, name, event)}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
    </div>
  )
}

export default DateInputField
